<template>
  <DatatableReportV3
    v-if="config"
    title="Payroll"
    flat
    :source="source"
    :config="config"
    :filters="filters"
    download-url="/reports/payroll"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import { Tenant } from "@/typings/tenant";

const store = useStore();
defineProps<{
  source: string;
  filters: Array<{ k: string; v: any }>;
}>();

const tenant = computed<Tenant | null>(() => store.state.tenant);

const getColumns = () => {
  const columns = [
    {
      data: "start_date",
      title: "Start Date",
      searchable: false,
      className: "payroll-start-date-col"
    },
    {
      data: "end_date",
      title: "End Date",
      searchable: false
    },
    { data: "employee", searchable: false, orderable: false },
    { data: "role", searchable: false },
    {
      data: "total_time",
      title: "Weekly Total",
      searchable: false,
      aggregateTitle: "Weekly Total",
      className: "text-right",
      footerClassName: "aggregate-number"
    },
    {
      data: "regular_time",
      title: "Regular",
      searchable: false,
      aggregateTitle: "Regular",
      className: "text-right",
      footerClassName: "aggregate-number"
    },
    {
      data: "overtime",
      title: "OT",
      searchable: false,
      aggregateTitle: "OT",
      className: "text-right",
      footerClassName: "aggregate-number"
    }
  ];

  if (tenant.value?.daily_overtime_enabled) {
    // Add weekly_regular_time
    // Add weekly_overtime
    // Add daily_overtime
    columns.splice(
      5,
      0,
      {
        data: "weekly_regular_time",
        title: "Weekly Regular",
        searchable: false,
        aggregateTitle: "Weekly Regular",
        className: "text-right",
        footerClassName: "aggregate-number"
      },
      {
        data: "weekly_overtime",
        title: "Weekly OT",
        searchable: false,
        aggregateTitle: "Weekly OT",
        className: "text-right",
        footerClassName: "aggregate-number"
      },
      {
        data: "daily_overtime",
        title: "Daily OT",
        searchable: false,
        aggregateTitle: "Daily OT",
        className: "text-right",
        footerClassName: "aggregate-number"
      }
    );
  }

  if (tenant.value?.daily_double_overtime_enabled) {
    // Add daily_regular_time BEFORE daily_overtime
    // Add daily_double_overtime AFTER daily_overtime
    // Add double_overtime to the end
    columns.splice(7, 0, {
      data: "daily_regular_time",
      title: "Daily Regular",
      searchable: false,
      aggregateTitle: "Daily Regular",
      className: "text-right",
      footerClassName: "aggregate-number"
    });
    columns.splice(9, 0, {
      data: "daily_double_overtime",
      title: "Daily Double OT",
      searchable: false,
      aggregateTitle: "Daily Double OT",
      className: "text-right",
      footerClassName: "aggregate-number"
    });
    columns.push({
      data: "double_overtime",
      title: "Double OT",
      searchable: false,
      aggregateTitle: "Double OT",
      className: "text-right",
      footerClassName: "aggregate-number"
    });
  }

  return columns;
};

interface Column {
  data: string;
  title?: string;
  searchable?: boolean;
  orderable?: boolean;
}

const config = ref<{ order: Array<any>; columns: Column[] } | null>(null);

onMounted(() => {
  if (tenant.value) {
    init();
  }
});

watch(tenant, (val, oldVal) => {
  if (val && !oldVal) {
    // First load
    init();
  }
});

const init = () => {
  config.value = {
    order: [[0, "desc"]],
    columns: getColumns()
  };
};
</script>

<style lang="scss">
.payroll-start-date-col {
  min-width: 150px !important;
}
</style>
